import _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  getKeyBySortOrderAndSortBy,
  getSortOrderAndSortBy,
  optionsSortDuration,
  optionsSortEventsCountBeats,
  optionsSortEventsRhythm,
} from '@/entities/event';
import type { FilterPopoverProps } from './index';

/**
 * Backs the events filter popover with draft filter state that is committed only when the user applies it. Holds local isHideReviewed, isCapture, isAvgHrBelow100 and sortOrder, resyncs them from eventFilter each time the popover opens, and on apply or reset writes the changed values back through setEventFilter and rewinds the page index to zero.
 *
 * @param props - Popover props holding eventFilter, setEventFilter, isOpen, setIsOpen, setPageIndex and the isShowCountBeats, isShowFilterDuration and optionrhythm layout flags.
 * @returns An object carrying the draft isHideReviewed, isCapture, isAvgHrBelow100 and sortOrder values plus the onChangeSortEvents, onSelectSortOption, onChangeSwitch, onChangeCheckbox, onClickReset and onClickApply handlers.
 */
const useFilterPopover = ({
  eventFilter,
  setEventFilter,
  isOpen,
  setIsOpen,
  setPageIndex,
  isShowCountBeats,
  isShowFilterDuration,
  optionrhythm,
}: Pick<
  FilterPopoverProps,
  | 'eventFilter'
  | 'setEventFilter'
  | 'isOpen'
  | 'setIsOpen'
  | 'setPageIndex'
  | 'isShowCountBeats'
  | 'isShowFilterDuration'
  | 'optionrhythm'
>) => {
  const [isHideReviewed, setIsHideReviewed] = useState(eventFilter.isHideReviewed);
  const [isCapture, setIsCapture] = useState(eventFilter.isCapture);
  const [isAvgHrBelow100, setIsAvgHrBelow100] = useState(eventFilter.isAvgHrBelow100 || false);
  const [sortOrder, setSortOrder] = useState(eventFilter.sortOrder);

  /**
   * Picks the draft sort order from the option list matching the current events tab. Chooses between the duration, count-beats and rhythm option lists, then stores the entry at the given index as the pending sort order.
   *
   * @param index - Position of the chosen option within the selected list.
   * @param isDuration - When true the duration option list is used instead of the event lists; defaults to false.
   * @returns Nothing.
   */
  const onChangeSortEvents = (index: number, isDuration = false) => {
    const list = isDuration
      ? optionsSortDuration
      : isShowCountBeats
        ? optionsSortEventsCountBeats
        : optionsSortEventsRhythm;
    const sortOrderOption = list[index];
    setSortOrder(sortOrderOption);
  };

  /**
   * Stores the sort option chosen from the popover dropdown as the draft sort order. Assigns the value straight to local state without touching the committed event filter.
   *
   * @param value - Selected sort order option, held until the filter is applied.
   * @returns Nothing.
   */
  const onSelectSortOption = (value: any) => {
    setSortOrder(value);
  };

  /**
   * Updates the draft hide-reviewed toggle from the popover switch. Ignores anything that is not a real click event so programmatic or synthetic changes leave the filter untouched.
   *
   * @param checked - New switch position to store as the hide-reviewed draft value.
   * @param event - Originating DOM event, applied only when its type is click.
   * @returns Nothing.
   */
  const onChangeSwitch = (checked: boolean, event: any) => {
    if (event?.type !== 'click') {
      return;
    }
    setIsHideReviewed(checked);
  };

  /**
   * Toggles one of the events filter checkboxes in the popover draft state. Inverts the captured-strip flag for the capturedStrip name and the average-heart-rate flag for AvgHrBelow100, ignoring any other name.
   *
   * @param name - Identifier of the checkbox, either capturedStrip or AvgHrBelow100.
   * @param value - Current checked state, which is inverted to produce the new one.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: string, value: any) => {
    if (name === 'capturedStrip') {
      setIsCapture(!value);
    }
    if (name === 'AvgHrBelow100') {
      setIsAvgHrBelow100(!value);
    }
  };

  /**
   * Clears every events filter, both the committed one and the popover draft. Writes an empty filter and rewinds the page index only when a filter or sort order was actually set, then blanks the local hide-reviewed, capture, average-heart-rate and sort order values and closes the popover.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    if (
      eventFilter.isHideReviewed ||
      eventFilter.isCapture ||
      eventFilter.isAvgHrBelow100 ||
      !_.isNil(eventFilter.sortOrder)
    ) {
      setEventFilter({
        isHideReviewed: false,
        isCapture: false,
        isAvgHrBelow100: false,
        sortOrder: undefined,
        sortBy: undefined,
      });
      setPageIndex({ index: 0 });
    }
    setIsHideReviewed(false);
    setIsCapture(false);
    setIsAvgHrBelow100(false);
    setSortOrder(undefined);
    setIsOpen(false);
  };

  /**
   * Commits the popover draft event filter and closes the popover. Resolves sortBy and the normalised sort order via getSortOrderAndSortBy using duration semantics when the tab shows durations rather than beat counts, writes the filter and rewinds the page index to zero only when something actually changed.
   *
   * @returns Nothing.
   */
  const onClickApply = () => {
    const isDuration = !isShowCountBeats && (isShowFilterDuration || optionrhythm);
    const { sortBy, newSortOrder } = getSortOrderAndSortBy(sortOrder, isDuration);
    if (
      isHideReviewed !== eventFilter.isHideReviewed ||
      isCapture !== eventFilter.isCapture ||
      isAvgHrBelow100 !== eventFilter.isAvgHrBelow100 ||
      newSortOrder !== eventFilter.sortOrder ||
      sortBy !== eventFilter.sortBy
    ) {
      setEventFilter({
        isHideReviewed,
        isCapture,
        isAvgHrBelow100,
        sortOrder: newSortOrder,
        sortBy,
      });
      setPageIndex({ index: 0 });
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setIsHideReviewed(eventFilter.isHideReviewed);
      setIsCapture(eventFilter.isCapture);
      setIsAvgHrBelow100(eventFilter.isAvgHrBelow100 || false);
      const orderKey = getKeyBySortOrderAndSortBy(
        eventFilter.sortBy,
        eventFilter.sortOrder,
        isShowCountBeats,
      );
      setSortOrder(orderKey);
    }
  }, [isOpen]);

  return {
    isHideReviewed,
    isCapture,
    isAvgHrBelow100,
    sortOrder,
    onChangeSortEvents,
    onSelectSortOption,
    onChangeSwitch,
    onChangeCheckbox,
    onClickReset,
    onClickApply,
  };
};

export default useFilterPopover;
